import React, { useRef, useEffect } from 'react';

import { IconChevronDown, IconChevronUp, IconX } from '@tabler/icons-react';

export interface DropdownOption {
  value: string;
  label: string;
  isDisabled?: boolean;
}

interface DropdownProps extends React.HTMLAttributes<HTMLDivElement> {
  options?: DropdownOption[]; // made optional to support children mode
  placeholder?: string;
  onValueChange?: (value: DropdownOption) => void;
  isOpen?: boolean;
  onToggleOpen?: (open: boolean) => void;
  value?: string;
  iconColor?: string;
  menuClasses?: string;
  disabled?: boolean;
  menuPlacement?: 'top' | 'bottom';
  variant?: 'primary' | 'gray';
  children?: React.ReactNode; // ✅ new
  showClear?: boolean; // ✅ new clear functionality
  onClear?: () => void; // ✅ optional custom clear handler
}

export const Dropdown: React.FC<DropdownProps> = ({
  options = [],
  placeholder = 'Select...',
  onValueChange,
  isOpen: controlledIsOpen,
  onToggleOpen,
  className,
  value,
  iconColor = 'pink-400',
  menuPlacement = 'bottom',
  menuClasses = '',
  variant = 'primary',
  disabled = false,
  children,
  showClear = false,
  onClear,
  ...props
}) => {
  const [internalIsOpen, setInternalIsOpen] = React.useState(false);
  const isOpen = controlledIsOpen ?? internalIsOpen;
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleOpen = (event: React.MouseEvent) => {
    // Don't toggle if clicking on the clear button
    if ((event.target as HTMLElement).closest('.clear-button')) {
      return;
    }

    if (!disabled) {
      const newState = !isOpen;
      setInternalIsOpen(newState);
      onToggleOpen?.(newState);
    }
  };

  const handleSelect = (option: DropdownOption) => {
    if (option.isDisabled) return;
    onValueChange?.(option);
    setInternalIsOpen(false);
    onToggleOpen?.(false);
  };

  const handleClear = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation(); // Prevent dropdown from opening
    if (onClear) {
      // Use custom clear handler if provided
      onClear();
    }
    // Also close the dropdown if it's open
    setInternalIsOpen(false);
    onToggleOpen?.(false);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setInternalIsOpen(false);
      onToggleOpen?.(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const topValue = options.length * 50;
  const placementClass = menuPlacement === 'bottom' ? 'mt-2' : `top-[-${topValue}px]`;
  const menuStyles = menuPlacement === 'bottom' ? {} : { top: `-${options.length * 37}px` };

  const disabledClass = disabled ? 'cursor-not-allowed' : '';

  const optionsArr = menuPlacement === 'bottom' ? options : [...options].reverse();

  // Padding lives on the trigger button (below), not the container, so the
  // button fills the entire box and a click anywhere inside it — including the
  // padding band around the label/caret — opens the menu. Same visual padding,
  // larger hit area.
  const baseContainerClass = 'relative text-sm border text-white';
  const containerVariantClass = {
    primary: 'rounded-md border-pink-500 bg-gray-950',
    gray: 'h-[28px] rounded-[4px] border-slate-400 bg-gray-950 text-[14px]',
  };
  const buttonPaddingClass = variant === 'gray' ? 'h-full py-1' : 'py-2';
  const baseMenuClass = 'absolute left-0 right-0 z-10 overflow-hidden shadow-lg';
  const menuVariantClass = {
    primary: 'rounded-md border border-pink-500 bg-gray-950',
    gray: 'rounded-[6px] border border-gray-950 bg-gray-950',
  };

  const hasValue = value && value.trim() !== '';
  const showClearIcon = showClear && hasValue && !disabled;

  const displayText = value
    ? (options.find((opt) => opt.value === value)?.label ?? value)
    : placeholder;

  return (
    <div
      ref={dropdownRef}
      className={`${className} ${baseContainerClass} ${disabledClass} ${containerVariantClass[variant]}`}
      {...props}
    >
      <button
        type="button"
        disabled={disabled}
        onClick={toggleOpen}
        className={`${disabledClass} flex w-full items-center justify-between px-4 ${buttonPaddingClass}`}
      >
        <span>{displayText}</span>
        <div className="flex items-center gap-1">
          {showClearIcon && (
            <span
              role="button"
              onClick={handleClear}
              onMouseDown={(e) => e.stopPropagation()} // Prevent mousedown from bubbling
              className="clear-button z-10 flex cursor-pointer items-center justify-center rounded p-1 hover:bg-gray-800"
              title="Clear selection"
            >
              <IconX className={`text-${iconColor}`} size={16} />
            </span>
          )}
          {isOpen ? (
            <IconChevronUp className={`text-${iconColor}`} size={20} />
          ) : (
            <IconChevronDown className={`text-${iconColor}`} size={20} />
          )}
        </div>
      </button>

      {isOpen &&
        (children ? (
          <div
            style={menuStyles}
            className={`${placementClass} ${menuClasses} ${baseMenuClass} ${menuVariantClass[variant]}`}
          >
            {children}
          </div>
        ) : (
          <ul
            style={menuStyles}
            className={`${placementClass} ${menuClasses} ${baseMenuClass} ${menuVariantClass[variant]}`}
          >
            {optionsArr.map((option) => (
              <li
                key={option.value}
                className={`px-4 py-2 text-sm ${
                  option.isDisabled
                    ? 'cursor-default text-gray-500'
                    : 'cursor-pointer text-white hover:bg-gray-900'
                }`}
                onClick={() => handleSelect(option)}
              >
                {option.label}
              </li>
            ))}
          </ul>
        ))}
    </div>
  );
};
